import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createBlock as _createBlock, normalizeStyle as _normalizeStyle } from "vue";
const _hoisted_1 = { class: "df-page" };
const _hoisted_2 = {
    key: 0,
    class: "df-state df-state--plain",
    role: "status"
};
const _hoisted_3 = { class: "df-state__text" };
const _hoisted_4 = { class: "df-state__card" };
const _hoisted_5 = { class: "df-state__title" };
const _hoisted_6 = { class: "df-state__body" };
const _hoisted_7 = { class: "df-state__card" };
const _hoisted_8 = { class: "df-state__title" };
const _hoisted_9 = { class: "df-state__body" };
const _hoisted_10 = {
    key: 4,
    class: "df-state df-state--plain"
};
const _hoisted_11 = { class: "df-state__card" };
const _hoisted_12 = { class: "df-state__title" };
const _hoisted_13 = { class: "df-state__body" };
const _hoisted_14 = {
    key: 5,
    class: "df-state df-state--plain"
};
const _hoisted_15 = { class: "df-state__card" };
const _hoisted_16 = { class: "df-state__title" };
const _hoisted_17 = { class: "df-state__body" };
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { DonationForm } from "@/donation-form";
import { brandingToThemeVars } from "@/donation-form";
import { getPublicForm } from "@/services/donationFormService";
import { useDonationSubmit } from "@/composables/useDonationSubmit";
import { useFunnel } from "@/composables/useFunnel";
import { logger } from "@/services/loggerService";
import { setLocale, docLocaleToHostTag, hostTagToDocLocale } from "@/shared/i18n";
import { parseUtmParameters } from "@/shared/utils/utm";
import { readAttributionToken } from "@/shared/utils/attribution";
const FIRST_STEP_ID = "amount";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DonationFormView',
    props: {
        slug: {}
    },
    setup(__props) {
        const props = __props;
        // Read once, synchronously at setup: a query string that mutates later must not rewrite this
        // visit's attribution. Never read it inside the byte-synced renderer, and never log it.
        const utm = parseUtmParameters(window.location.search);
        const attributionToken = readAttributionToken(window.location.search);
        const { onSubmit, errorMessage } = useDonationSubmit(props.slug);
        const form = ref(null);
        // Back from the payment page restores this page from the bfcache with the Donate button still busy.
        function onPageShow(event) {
            if (event.persisted) {
                form.value?.resetSubmit();
            }
        }
        window.addEventListener("pageshow", onPageShow);
        onBeforeUnmount(() => window.removeEventListener("pageshow", onPageShow));
        // Submitted/RedirectedToPayment fire in useDonationSubmit on the same module-scoped session token.
        const funnel = useFunnel(props.slug);
        function onStepChange(stepId) {
            if (stepId !== FIRST_STEP_ID) {
                funnel.started();
            }
        }
        const { t, locale } = useI18n();
        const router = useRouter();
        const state = ref("loading");
        const doc = ref(null);
        const initialDocLocale = computed(() => {
            if (!doc.value) {
                return undefined;
            }
            const fromHost = hostTagToDocLocale(locale.value);
            return doc.value.supportedLocales.includes(fromHost) ? fromHost : doc.value.defaultLocale;
        });
        const brandStyle = computed(() => doc.value ? brandingToThemeVars(doc.value.branding) : {});
        const closedMessage = computed(() => {
            const fallback = t("donationForm.closed.body");
            if (!doc.value) {
                return fallback;
            }
            const cancelled = doc.value.messages.find((m) => m.type === "Cancelled" && m.isActive);
            if (!cancelled) {
                return fallback;
            }
            const lang = initialDocLocale.value ?? doc.value.defaultLocale;
            return cancelled.content[lang] ?? cancelled.content[doc.value.defaultLocale] ?? fallback;
        });
        function onLocaleChange(code) {
            setLocale(docLocaleToHostTag(code));
        }
        // The submit error, <html lang> and the closed/empty screens' chrome are host-resolved: keep the
        // host on the form's language.
        function alignHostLocale() {
            const docLocale = initialDocLocale.value;
            if (docLocale === undefined) {
                return;
            }
            const stem = (tag) => tag.split("-")[0];
            if (stem(hostTagToDocLocale(locale.value)) !== stem(docLocale)) {
                // Never through setLocale: it persists, and the form's language must not outlive this form.
                const tag = docLocaleToHostTag(docLocale);
                locale.value = tag;
                document.documentElement.lang = tag;
            }
        }
        onMounted(async () => {
            try {
                const result = await getPublicForm(props.slug);
                if (result.kind === "not-found") {
                    state.value = "not-found";
                    return;
                }
                if (result.kind === "redirect") {
                    // replace, never push: Back must not return to the dead slug. App.vue keys <router-view> on
                    // the path, so this param-only navigation remounts the view (useDonationSubmit/useFunnel
                    // capture the slug by value at setup). Awaited so a guard throw lands in the catch.
                    // Keep the query string: the remounted destination view captures its attribution from it.
                    await router.replace(`/forms/${encodeURIComponent(result.slug)}${window.location.search}`);
                    return;
                }
                doc.value = result.value.document;
                alignHostLocale();
                if (result.value.status === "Paused") {
                    state.value = "closed";
                    return;
                }
                const hasOptions = result.value.document.allowedFrequencies.length > 0 ||
                    result.value.document.amountPresets.length > 0;
                state.value = hasOptions ? "ready" : "empty";
                if (state.value === "ready") {
                    funnel.viewed();
                }
            }
            catch (e) {
                state.value = "error";
                logger.error("Failed to load the public donation form", { slug: props.slug, error: String(e) });
            }
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("main", _hoisted_1, [
                (state.value === 'loading')
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _cache[0] || (_cache[0] = _createElementVNode("span", {
                            class: "df-state__spinner",
                            "aria-hidden": "true"
                        }, null, -1 /* CACHED */)),
                        _createElementVNode("p", _hoisted_3, _toDisplayString(_ctx.$t("common.loading")), 1 /* TEXT */)
                    ]))
                    : (state.value === 'ready' && doc.value)
                        ? (_openBlock(), _createBlock(_unref(DonationForm), {
                            key: 1,
                            ref_key: "form",
                            ref: form,
                            document: doc.value,
                            "preview-mode": false,
                            "initial-locale": initialDocLocale.value,
                            utm: _unref(utm),
                            "attribution-token": _unref(attributionToken),
                            "on-submit": _unref(onSubmit),
                            "submit-error": _unref(errorMessage),
                            onLocaleChange: onLocaleChange,
                            onStepChange: onStepChange
                        }, null, 8 /* PROPS */, ["document", "initial-locale", "utm", "attribution-token", "on-submit", "submit-error"]))
                        : (state.value === 'empty' && doc.value)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 2,
                                class: "df-state df-state--branded",
                                style: _normalizeStyle(brandStyle.value)
                            }, [
                                _createElementVNode("div", _hoisted_4, [
                                    _createElementVNode("h1", _hoisted_5, _toDisplayString(_ctx.$t("donationForm.empty.title")), 1 /* TEXT */),
                                    _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.$t("donationForm.empty.body")), 1 /* TEXT */)
                                ])
                            ], 4 /* STYLE */))
                            : (state.value === 'closed' && doc.value)
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 3,
                                    class: "df-state df-state--branded",
                                    style: _normalizeStyle(brandStyle.value)
                                }, [
                                    _createElementVNode("div", _hoisted_7, [
                                        _createElementVNode("h1", _hoisted_8, _toDisplayString(_ctx.$t("donationForm.closed.title")), 1 /* TEXT */),
                                        _createElementVNode("p", _hoisted_9, _toDisplayString(closedMessage.value), 1 /* TEXT */)
                                    ])
                                ], 4 /* STYLE */))
                                : (state.value === 'not-found')
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                        _createElementVNode("div", _hoisted_11, [
                                            _createElementVNode("h1", _hoisted_12, _toDisplayString(_ctx.$t("donationForm.notFound.title")), 1 /* TEXT */),
                                            _createElementVNode("p", _hoisted_13, _toDisplayString(_ctx.$t("donationForm.notFound.body")), 1 /* TEXT */)
                                        ])
                                    ]))
                                    : (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                        _createElementVNode("div", _hoisted_15, [
                                            _createElementVNode("h1", _hoisted_16, _toDisplayString(_ctx.$t("donationForm.error.title")), 1 /* TEXT */),
                                            _createElementVNode("p", _hoisted_17, _toDisplayString(_ctx.$t("donationForm.error.body")), 1 /* TEXT */)
                                        ])
                                    ]))
            ]));
        };
    }
});
